import {
  getDocumentById,
  getCollection,
  addDocToCollection,
  updateDocById,
} from '../_shared/firestore/index.js';
import { SafetyActionModel } from '@cmms/models';
import { createCMMSView } from '../views/views.js';
import { getAuth } from 'firebase/auth';

const COLLECTION_NAME = 'safety_actions';

// Helper function to get current user's company ID
async function getCurrentUserCompanyId(db) {
  console.log('🔧 getCurrentUserCompanyId (Safety Actions): Starting...');
  
  const auth = getAuth(db.app);
  const currentUser = auth.currentUser;
  
  console.log('🔧 getCurrentUserCompanyId (Safety Actions): currentUser:', currentUser ? 'authenticated' : 'not authenticated');
  
  if (!currentUser) {
    console.error('🔧 getCurrentUserCompanyId (Safety Actions): No authenticated user');
    throw new Error('User not authenticated');
  }

  console.log('🔧 getCurrentUserCompanyId (Safety Actions): Getting user doc for uid:', currentUser.uid);
  const userDoc = await getDocumentById(db, 'users', currentUser.uid);
  
  console.log('🔧 getCurrentUserCompanyId (Safety Actions): userDoc:', userDoc);
  
  if (!userDoc || !userDoc.company_id) {
    console.error('🔧 getCurrentUserCompanyId (Safety Actions): User company not found in userDoc:', userDoc);
    throw new Error('User company not found');
  }

  console.log('🔧 getCurrentUserCompanyId (Safety Actions): Found companyId:', userDoc.company_id);
  return userDoc.company_id;
}

async function getActionsByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, {
      key: 'company_id',
      operator: '==',
      value: companyId,
    });
    const items = docs.map((doc) => ({ id: doc.$id, model: new SafetyActionModel(doc) }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getActionsByAssignee(db, companyId, userId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'company_id', operator: '==', value: companyId },
      { key: 'assigned_to_user_id', operator: '==', value: userId },
    ]);
    const items = docs.map((doc) => ({ id: doc.$id, model: new SafetyActionModel(doc) }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getActionsByReportId(db, reportId) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const docs = await getCollection(db, COLLECTION_NAME, [
      { key: 'related_report_id', operator: '==', value: reportId },
      { key: 'company_id', operator: '==', value: companyId }
    ]);
    const items = docs.map((doc) => ({ id: doc.$id, model: new SafetyActionModel(doc) }));
    return createCMMSView({ data: items });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function createAction(db, payload) {
  try {
    console.log('🔧 createAction: Starting with payload:', payload);
    
    const companyId = await getCurrentUserCompanyId(db);
    const auth = getAuth(db.app);
    const currentUser = auth.currentUser;
    const userId = currentUser ? currentUser.uid : '';
    
    console.log('🔧 createAction: Got companyId:', companyId);
    console.log('🔧 createAction: Current user ID:', userId);
    
    const payloadWithCompany = {
      ...payload,
      company_id: companyId,
      created_by: userId // Add the current user as creator
    };
    console.log('🔧 createAction: payloadWithCompany:', payloadWithCompany);
    
    const model = new SafetyActionModel(payloadWithCompany);
    console.log('🔧 createAction: Created model, calling toFirestore()');
    
    const firestoreData = model.toFirestore();
    console.log('🔧 createAction: Firestore data to save:', firestoreData);
    console.log('🔧 createAction: Attempting to save to collection:', COLLECTION_NAME);

    const ref = await addDocToCollection(db, COLLECTION_NAME, firestoreData);
    console.log('🔧 createAction: Successfully created action with ref:', ref);

    return createCMMSView({ data: ref });
  } catch (error) {
    console.error('🔧 createAction: Error occurred:', error);
    console.error('🔧 createAction: Error message:', error.message);
    console.error('🔧 createAction: Error stack:', error.stack);
    return createCMMSView({ error });
  }
}

async function updateAction(db, id, updateData) {
  try {
    const companyId = await getCurrentUserCompanyId(db);
    const existing = await getDocumentById(db, COLLECTION_NAME, id);
    if (!existing) return createCMMSView({ error: 'Action not found' });
    if (existing.company_id !== companyId) {
      return createCMMSView({ error: 'Access denied: Action not found' });
    }
    const merged = { ...existing, ...updateData, updated_at: Date.now() };
    const normalized = new SafetyActionModel(merged).toFirestore();
    const finalUpdate = { ...merged, ...normalized };
    await updateDocById(db, COLLECTION_NAME, id, finalUpdate);
    return createCMMSView({ data: { id, ...finalUpdate } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

export {
  getActionsByCompany,
  getActionsByAssignee,
  getActionsByReportId,
  createAction,
  updateAction,
};


